// frontend/src/pages/MovimientosBodegaPage.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import bodegaService from '../services/bodegaService';
import {
  recursoService,
  proyectoService,
  presupuestoCostoService,
  presupuestoCostoAreaService
} from '../services/onebaseService';
import api from '../services/api';
import Loading from '../components/Loading';
import './MovimientosBodegaPage.css';

const MovimientosBodegaPage = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Estados para presupuestos (para salidas)
  const [proyectos, setProyectos] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [recursosPresupuestarios, setRecursosPresupuestarios] = useState([]);

  // Datos del encabezado del movimiento
  const [formData, setFormData] = useState({
    tipo_movimiento: 'entrada',
    bodega: '',
    bodega_destino: '',
    motivo: 'otro',
    descripcion: '',
    tipo_documento: '',
    numero_documento: ''
  });

  // Detalles (recursos) del movimiento
  const [detalles, setDetalles] = useState([]);

  // Detalle temporal para agregar
  const [detalleTemp, setDetalleTemp] = useState({
    recurso: '',
    cantidad: 0,
    precio_unitario: 0,
    observaciones: '',
    // Campos para presupuesto (solo para salidas)
    proyecto: '',
    presupuesto_costo: '',
    area: '',
    partida: '',
    presupuesto_costo_recurso: ''
  });

  // Estado para edición de detalles
  const [editingDetalleIndex, setEditingDetalleIndex] = useState(null);

  // Estado para stock disponible (para validación en salidas)
  const [stockDisponible, setStockDisponible] = useState(null);

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    recurso: '',
    bodega: '',
    tipo_movimiento: '',
    motivo: '',
    tipo_documento: ''
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [movRes, bodRes, recRes, proyRes] = await Promise.all([
        bodegaService.getMovimientos(),
        bodegaService.getBodegas(),
        recursoService.getAll({ stock: true }), // Solo recursos con stock habilitado
        proyectoService.getAll()
      ]);
      // La API devuelve un objeto paginado con la estructura: { count, next, previous, results }
      setMovimientos(movRes.data.results || movRes.data);
      setBodegas(bodRes.data.results || bodRes.data);
      setRecursos(recRes.data.results || recRes.data);
      setProyectos(proyRes.data.results || proyRes.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener lista única de recursos que están en los movimientos
  const getRecursosEnMovimientos = () => {
    const recursosSet = new Set();
    movimientos.forEach(mov => {
      if (mov.detalles && mov.detalles.length > 0) {
        mov.detalles.forEach(detalle => {
          if (detalle.recurso && detalle.recurso_nombre) {
            recursosSet.add(JSON.stringify({
              id: detalle.recurso,
              nombre: detalle.recurso_nombre
            }));
          }
        });
      }
    });
    return Array.from(recursosSet).map(r => JSON.parse(r));
  };

  // Filtrar movimientos según los filtros aplicados
  const movimientosFiltrados = movimientos.filter(mov => {
    // Filtro por recurso (debe estar en algún detalle)
    if (filtros.recurso) {
      const tieneRecurso = mov.detalles?.some(det => det.recurso === parseInt(filtros.recurso));
      if (!tieneRecurso) return false;
    }

    // Filtro por bodega
    if (filtros.bodega && mov.bodega !== parseInt(filtros.bodega)) {
      return false;
    }

    // Filtro por tipo de movimiento
    if (filtros.tipo_movimiento && mov.tipo_movimiento !== filtros.tipo_movimiento) {
      return false;
    }

    // Filtro por motivo
    if (filtros.motivo && mov.motivo !== filtros.motivo) {
      return false;
    }

    // Filtro por tipo de documento
    if (filtros.tipo_documento && mov.tipo_documento !== filtros.tipo_documento) {
      return false;
    }

    return true;
  });

  // Cargar presupuestos al seleccionar proyecto
  const handleProyectoChange = async (proyectoId) => {
    setDetalleTemp({
      ...detalleTemp,
      proyecto: proyectoId,
      presupuesto_costo: '',
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setPresupuestos([]);
    setAreas([]);
    setPartidas([]);
    setRecursosPresupuestarios([]);

    if (proyectoId) {
      try {
        const res = await presupuestoCostoService.getAll({ proyecto: proyectoId });
        setPresupuestos(res.data.results || res.data);
      } catch (error) {
        console.error('Error al cargar presupuestos:', error);
      }
    }
  };

  // Cargar áreas al seleccionar presupuesto
  const handlePresupuestoChange = async (presupuestoId) => {
    setDetalleTemp({
      ...detalleTemp,
      presupuesto_costo: presupuestoId,
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setAreas([]);
    setPartidas([]);
    setRecursosPresupuestarios([]);

    if (presupuestoId) {
      try {
        const res = await presupuestoCostoAreaService.getAll({ presupuesto_costo: presupuestoId });
        setAreas(res.data.results || res.data);
      } catch (error) {
        console.error('Error al cargar áreas:', error);
      }
    }
  };

  // Cargar partidas al seleccionar área
  const handleAreaChange = async (areaId) => {
    setDetalleTemp({
      ...detalleTemp,
      area: areaId,
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setPartidas([]);
    setRecursosPresupuestarios([]);

    if (areaId) {
      try {
        // Derivar URL base para OneBase desde la baseURL actual de `api`
        const baseURL = api?.defaults?.baseURL || '/api/content';
        const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');

        const res = await api.get(`${API_BASE_URL}/presupuestos-costo-partidas/`, {
          params: { area: areaId }
        });
        setPartidas(res.data.results || res.data);
      } catch (error) {
        console.error('Error al cargar partidas:', error);
      }
    }
  };

  // Cargar recursos presupuestarios al seleccionar partida
  const handlePartidaChange = async (partidaId) => {
    setDetalleTemp({
      ...detalleTemp,
      partida: partidaId,
      presupuesto_costo_recurso: ''
    });
    setRecursosPresupuestarios([]);

    if (partidaId) {
      try {
        const res = await presupuestoCostoService.getRecursos(partidaId);
        setRecursosPresupuestarios(res.data.results || res.data);
      } catch (error) {
        console.error('Error al cargar recursos presupuestarios:', error);
      }
    }
  };

  // Cargar precio del stock cuando se selecciona un recurso en una salida
  const handleRecursoChange = async (recursoId) => {
    setDetalleTemp({...detalleTemp, recurso: recursoId});
    setStockDisponible(null); // Resetear stock disponible

    // Si es una salida, obtener el precio y cantidad disponible del stock
    if (formData.tipo_movimiento === 'salida' && recursoId && formData.bodega) {
      try {
        const res = await bodegaService.getStocks({
          bodega: formData.bodega,
          recurso: recursoId
        });

        const stocks = res.data.results || res.data;
        if (stocks.length > 0) {
          const stock = stocks[0];
          // Autocompletar el precio con el precio_costo_unitario del stock (PPP)
          setDetalleTemp(prev => ({
            ...prev,
            recurso: recursoId,
            precio_unitario: stock.precio_costo_unitario || 0
          }));
          // Guardar stock disponible para validación
          setStockDisponible(parseFloat(stock.cantidad_actual || 0));
        } else {
          // No hay stock disponible
          setStockDisponible(0);
        }
      } catch (error) {
        console.error('Error al cargar precio del stock:', error);
        setStockDisponible(0);
      }
    } else {
      // Para entradas y otros tipos, no hay restricción de stock
      setStockDisponible(null);
    }
  };

  // Manejar cambio de cantidad con validación en tiempo real
  const handleCantidadChange = (valor) => {
    const cantidad = parseFloat(valor) || 0;
    setDetalleTemp({...detalleTemp, cantidad: valor});

    // Validación en tiempo real para salidas
    if (formData.tipo_movimiento === 'salida' && stockDisponible !== null) {
      if (cantidad > stockDisponible) {
        // La cantidad excede el stock disponible
        console.warn(`Cantidad solicitada (${cantidad}) excede stock disponible (${stockDisponible})`);
      }
    }
  };

  const agregarDetalle = () => {
    if (!detalleTemp.recurso || detalleTemp.cantidad <= 0) {
      toast.error('Debe seleccionar un recurso y una cantidad válida');
      return;
    }

    // Validar stock disponible para salidas
    if (formData.tipo_movimiento === 'salida' && stockDisponible !== null) {
      const cantidadSolicitada = parseFloat(detalleTemp.cantidad);
      if (cantidadSolicitada > stockDisponible) {
        toast.error(`Stock insuficiente. Disponible: ${stockDisponible.toFixed(2)}, Solicitado: ${cantidadSolicitada.toFixed(2)}`);
        return;
      }
    }

    // Validar presupuesto_costo_recurso para salidas
    if (formData.tipo_movimiento === 'salida' && !detalleTemp.presupuesto_costo_recurso) {
      toast.error('Para movimientos de salida, debe seleccionar un recurso presupuestario');
      return;
    }

    const recursoSeleccionado = recursos.find(r => r.id === parseInt(detalleTemp.recurso));
    const recursoPresupuestarioSeleccionado = recursosPresupuestarios.find(
      rp => rp.id === parseInt(detalleTemp.presupuesto_costo_recurso)
    );
    const proyectoSeleccionado = proyectos.find(p => p.id === parseInt(detalleTemp.proyecto));

    if (editingDetalleIndex !== null) {
      // Modo edición: actualizar el detalle existente
      const nuevosDetalles = [...detalles];
      nuevosDetalles[editingDetalleIndex] = {
        ...detalleTemp,
        recurso_nombre: recursoSeleccionado?.nombre || '',
        recurso_unidad: recursoSeleccionado?.unidad || '',
        presupuesto_costo_recurso_nombre: recursoPresupuestarioSeleccionado?.nombre || '',
        proyecto_nombre: proyectoSeleccionado?.nombre || ''
      };
      setDetalles(nuevosDetalles);
      setEditingDetalleIndex(null);
    } else {
      // Modo agregar: añadir nuevo detalle
      setDetalles([...detalles, {
        ...detalleTemp,
        recurso_nombre: recursoSeleccionado?.nombre || '',
        recurso_unidad: recursoSeleccionado?.unidad || '',
        presupuesto_costo_recurso_nombre: recursoPresupuestarioSeleccionado?.nombre || '',
        proyecto_nombre: proyectoSeleccionado?.nombre || ''
      }]);
    }

    setDetalleTemp({
      recurso: '',
      cantidad: 0,
      precio_unitario: 0,
      observaciones: '',
      proyecto: '',
      presupuesto_costo: '',
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setStockDisponible(null);
  };

  const editarDetalle = (index) => {
    const detalle = detalles[index];
    setDetalleTemp({
      recurso: detalle.recurso,
      cantidad: detalle.cantidad,
      precio_unitario: detalle.precio_unitario,
      observaciones: detalle.observaciones,
      proyecto: detalle.proyecto || '',
      presupuesto_costo: detalle.presupuesto_costo || '',
      area: detalle.area || '',
      partida: detalle.partida || '',
      presupuesto_costo_recurso: detalle.presupuesto_costo_recurso || ''
    });
    setEditingDetalleIndex(index);

    // Cargar datos en cascada si hay valores
    if (detalle.proyecto) {
      handleProyectoChange(detalle.proyecto);
    }
    if (detalle.presupuesto_costo) {
      handlePresupuestoChange(detalle.presupuesto_costo);
    }
    if (detalle.area) {
      handleAreaChange(detalle.area);
    }
    if (detalle.partida) {
      handlePartidaChange(detalle.partida);
    }
  };

  const eliminarDetalle = (index) => {
    setDetalles(detalles.filter((_, i) => i !== index));
    // Si estamos editando el detalle que se está eliminando, cancelar la edición
    if (editingDetalleIndex === index) {
      setEditingDetalleIndex(null);
      setDetalleTemp({
        recurso: '',
        cantidad: 0,
        precio_unitario: 0,
        observaciones: '',
        proyecto: '',
        presupuesto_costo: '',
        area: '',
        partida: '',
        presupuesto_costo_recurso: ''
      });
      setStockDisponible(null);
    } else if (editingDetalleIndex !== null && editingDetalleIndex > index) {
      // Ajustar el índice de edición si se elimina un elemento anterior
      setEditingDetalleIndex(editingDetalleIndex - 1);
    }
  };

  const cancelarEdicion = () => {
    setEditingDetalleIndex(null);
    setDetalleTemp({
      recurso: '',
      cantidad: 0,
      precio_unitario: 0,
      observaciones: '',
      proyecto: '',
      presupuesto_costo: '',
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setStockDisponible(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (detalles.length === 0) {
      toast.error('Debe agregar al menos un recurso al movimiento');
      return;
    }

    try {
      const payload = {
        ...formData,
        detalles_data: detalles.map(d => ({
          recurso: d.recurso,
          cantidad: parseFloat(d.cantidad),
          precio_unitario: parseFloat(d.precio_unitario) || 0,
          observaciones: d.observaciones || '',
          presupuesto_costo_recurso: d.presupuesto_costo_recurso || null
        }))
      };

      if (isEditMode) {
        await bodegaService.updateMovimiento(editingId, payload);
        toast.success('Movimiento actualizado exitosamente');
      } else {
        await bodegaService.createMovimiento(payload);
        toast.success('Movimiento registrado exitosamente');
      }

      setShowModal(false);
      resetForm();
      cargarDatos();
    } catch (error) {
      console.error('Error al registrar movimiento:', error);

      // Extraer errores de la respuesta
      const errorData = error.response?.data || {};

      // Función para mostrar errores (puede ser string o array)
      const mostrarErrores = (errores) => {
        if (Array.isArray(errores)) {
          errores.forEach(err => toast.error(err));
        } else if (typeof errores === 'string') {
          toast.error(errores);
        }
      };

      // Manejar diferentes tipos de errores
      if (errorData.non_field_errors) {
        mostrarErrores(errorData.non_field_errors);
      } else if (errorData.detalles_data) {
        mostrarErrores(errorData.detalles_data);
      } else if (errorData.detail) {
        mostrarErrores(errorData.detail);
      } else {
        // Mostrar todos los errores del objeto
        Object.entries(errorData).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errors.forEach(err => toast.error(`${field}: ${err}`));
          } else if (typeof errors === 'string') {
            toast.error(`${field}: ${errors}`);
          } else if (typeof errors === 'object' && errors !== null) {
            // Para errores anidados
            toast.error(JSON.stringify(errors));
          }
        });

        // Si no hay errores específicos, mostrar mensaje genérico
        if (Object.keys(errorData).length === 0) {
          toast.error('Error al registrar movimiento');
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_movimiento: 'entrada',
      bodega: '',
      bodega_destino: '',
      motivo: 'otro',
      descripcion: '',
      tipo_documento: '',
      numero_documento: ''
    });
    setDetalles([]);
    setDetalleTemp({
      recurso: '',
      cantidad: 0,
      precio_unitario: 0,
      observaciones: '',
      proyecto: '',
      presupuesto_costo: '',
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setIsEditMode(false);
    setEditingId(null);
    setEditingDetalleIndex(null);
    setStockDisponible(null);
    // Limpiar listas de presupuesto
    setPresupuestos([]);
    setAreas([]);
    setPartidas([]);
    setRecursosPresupuestarios([]);
  };

  const handleEdit = (movimiento) => {
    // Cargar datos del movimiento en el formulario
    setFormData({
      tipo_movimiento: movimiento.tipo_movimiento,
      bodega: movimiento.bodega,
      bodega_destino: movimiento.bodega_destino || '',
      motivo: movimiento.motivo,
      descripcion: movimiento.descripcion || '',
      tipo_documento: movimiento.tipo_documento || '',
      numero_documento: movimiento.numero_documento || ''
    });

    // Cargar detalles
    const detallesFormateados = movimiento.detalles.map(det => ({
      recurso: det.recurso,
      recurso_nombre: det.recurso_nombre,
      recurso_unidad: det.recurso_unidad,
      cantidad: det.cantidad,
      precio_unitario: det.precio_unitario,
      observaciones: det.observaciones,
      presupuesto_costo_recurso: det.presupuesto_costo_recurso || '',
      presupuesto_costo_recurso_nombre: det.presupuesto_costo_recurso_nombre || '',
      proyecto_nombre: det.proyecto_nombre || ''
    }));
    setDetalles(detallesFormateados);

    setIsEditMode(true);
    setEditingId(movimiento.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este movimiento? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await bodegaService.deleteMovimiento(id);
      toast.success('Movimiento eliminado exitosamente');
      cargarDatos();
    } catch (error) {
      console.error('Error al eliminar movimiento:', error);

      // Extraer errores de la respuesta
      const errorData = error.response?.data || {};

      // Función para mostrar errores (puede ser string o array)
      const mostrarErrores = (errores) => {
        if (Array.isArray(errores)) {
          errores.forEach(err => toast.error(err));
        } else if (typeof errores === 'string') {
          toast.error(errores);
        }
      };

      // Manejar diferentes tipos de errores
      if (errorData.non_field_errors) {
        mostrarErrores(errorData.non_field_errors);
      } else if (errorData.detail) {
        mostrarErrores(errorData.detail);
      } else if (Object.keys(errorData).length > 0) {
        // Mostrar todos los errores del objeto
        Object.entries(errorData).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errors.forEach(err => toast.error(`${field}: ${err}`));
          } else if (typeof errors === 'string') {
            toast.error(`${field}: ${errors}`);
          }
        });
      } else {
        toast.error('Error al eliminar el movimiento');
      }
    }
  };

  const handleCompletar = async (id) => {
    if (!window.confirm('¿Está seguro de completar este movimiento? Esto afectará el inventario.')) {
      return;
    }

    try {
      await bodegaService.updateMovimiento(id, { estado: 'completado' });
      toast.success('Movimiento completado exitosamente. El inventario ha sido actualizado.');
      cargarDatos();
    } catch (error) {
      console.error('Error al completar movimiento:', error);

      // Extraer errores de la respuesta
      const errorData = error.response?.data || {};

      // Función para mostrar errores (puede ser string o array)
      const mostrarErrores = (errores) => {
        if (Array.isArray(errores)) {
          errores.forEach(err => toast.error(err));
        } else if (typeof errores === 'string') {
          toast.error(errores);
        }
      };

      // Manejar diferentes tipos de errores
      if (errorData.non_field_errors) {
        mostrarErrores(errorData.non_field_errors);
      } else if (errorData.detail) {
        mostrarErrores(errorData.detail);
      } else if (Object.keys(errorData).length > 0) {
        // Mostrar todos los errores del objeto
        Object.entries(errorData).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errors.forEach(err => toast.error(`${field}: ${err}`));
          } else if (typeof errors === 'string') {
            toast.error(`${field}: ${errors}`);
          }
        });
      } else {
        toast.error('Error al completar el movimiento');
      }
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="movimientos-page">
      <div className="page-header">
        <h1>📝 Movimientos de Inventario</h1>
        <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          ➕ Nuevo Movimiento
        </button>
      </div>

      {/* Barra de filtros */}
      <div className="filters-bar" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1.5rem',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
            Recurso
          </label>
          <select
            value={filtros.recurso}
            onChange={(e) => setFiltros({...filtros, recurso: e.target.value})}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          >
            <option value="">Todos los recursos</option>
            {getRecursosEnMovimientos().map(rec => (
              <option key={rec.id} value={rec.id}>{rec.nombre}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
            Bodega
          </label>
          <select
            value={filtros.bodega}
            onChange={(e) => setFiltros({...filtros, bodega: e.target.value})}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          >
            <option value="">Todas las bodegas</option>
            {bodegas.map(bod => (
              <option key={bod.id} value={bod.id}>{bod.nombre}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
            Tipo
          </label>
          <select
            value={filtros.tipo_movimiento}
            onChange={(e) => setFiltros({...filtros, tipo_movimiento: e.target.value})}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          >
            <option value="">Todos los tipos</option>
            <option value="entrada">Entrada</option>
            <option value="salida">Salida</option>
            <option value="ajuste">Ajuste</option>
            <option value="traspaso">Traspaso</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
            Motivo
          </label>
          <select
            value={filtros.motivo}
            onChange={(e) => setFiltros({...filtros, motivo: e.target.value})}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          >
            <option value="">Todos los motivos</option>
            <option value="compra">Compra</option>
            <option value="devolucion">Devolución</option>
            <option value="produccion">Producción</option>
            <option value="venta">Venta</option>
            <option value="uso_interno">Uso Interno</option>
            <option value="merma">Merma</option>
            <option value="ajuste_inventario">Ajuste de Inventario</option>
            <option value="traspaso_bodega">Traspaso entre Bodegas</option>
            <option value="otro">Otro</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
            Tipo Documento
          </label>
          <select
            value={filtros.tipo_documento}
            onChange={(e) => setFiltros({...filtros, tipo_documento: e.target.value})}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          >
            <option value="">Todos los documentos</option>
            <option value="factura">Factura</option>
            <option value="orden_compra">Orden de compra</option>
            <option value="guia_despacho">Guía de despacho</option>
            <option value="boleta">Boleta</option>
          </select>
        </div>

        {(filtros.recurso || filtros.bodega || filtros.tipo_movimiento || filtros.motivo || filtros.tipo_documento) && (
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => setFiltros({
                recurso: '',
                bodega: '',
                tipo_movimiento: '',
                motivo: '',
                tipo_documento: ''
              })}
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}
            >
              🗑️ Limpiar Filtros
            </button>
          </div>
        )}
      </div>

      <div className="movimientos-table-container">
        <table className="movimientos-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Status</th>
              <th>Bodega</th>
              <th>Items</th>
              <th>Motivo</th>
              <th>Tipo Documento</th>
              <th>Doc.</th>
              <th>Usuario</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {movimientosFiltrados.length === 0 ? (
              <tr><td colSpan="10" className="empty-row">
                {movimientos.length === 0 ? 'No hay movimientos registrados' : 'No hay movimientos que coincidan con los filtros'}
              </td></tr>
            ) : (
              movimientosFiltrados.map((mov) => (
                <tr key={mov.id}>
                  <td>{new Date(mov.fecha_movimiento).toLocaleString()}</td>
                  <td>
                    <span className={`badge-tipo ${mov.tipo_movimiento}`}>
                      {mov.tipo_movimiento_display}
                    </span>
                  </td>
                  <td>
                    <span className={`badge-status ${mov.status}`}>
                      {mov.status_display}
                    </span>
                  </td>
                  <td>
                    {mov.bodega_nombre}
                    {mov.bodega_destino_nombre && ` → ${mov.bodega_destino_nombre}`}
                  </td>
                  <td>
                    {mov.detalles && mov.detalles.length > 0 ? (
                      <div className="detalles-summary">
                        {mov.detalles.length} recurso{mov.detalles.length > 1 ? 's' : ''}
                        <details className="detalles-dropdown">
                          <summary>Ver detalles</summary>
                          <ul>
                            {mov.detalles.map((det, idx) => (
                              <li key={idx}>
                                {det.recurso_nombre}: {parseFloat(det.cantidad).toFixed(2)} {det.recurso_unidad}
                                {det.precio_unitario > 0 && ` - $${parseFloat(det.precio_unitario).toLocaleString()}`}
                              </li>
                            ))}
                          </ul>
                        </details>
                      </div>
                    ) : (
                      <span>Sin detalles</span>
                    )}
                  </td>
                  <td>{mov.motivo_display}</td>
                  <td>{mov.tipo_documento_display || '-'}</td>
                  <td>{mov.numero_documento || '-'}</td>
                  <td>{mov.created_by_nombre}</td>
                  <td>
                    <div style={{display: 'flex', gap: '5px', justifyContent: 'center'}}>
                      {mov.estado === 'borrador' && (
                        <button
                          className="btn-edit-small"
                          onClick={() => handleCompletar(mov.id)}
                          title="Completar"
                          style={{backgroundColor: '#10b981', color: 'white'}}
                        >
                          ✓
                        </button>
                      )}
                      <button
                        className="btn-edit-small"
                        onClick={() => handleEdit(mov)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete-small"
                        onClick={() => handleDelete(mov.id)}
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay-fullscreen" onClick={() => setShowModal(false)}>
          <div className="modal-fullscreen" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-fullscreen">
              <div className="header-info">
                <h2>{isEditMode ? '✏️ Editar Movimiento' : '➕ Nuevo Movimiento'}</h2>
              </div>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-fullscreen-form">
              <div className="tab-content-area">
                {/* Encabezado del movimiento */}
                <h3>Información General</h3>
              <div className="form-grid-5cols">
                <div className="form-group">
                  <label>Tipo de Movimiento *</label>
                  <select
                    value={formData.tipo_movimiento}
                    onChange={(e) => setFormData({...formData, tipo_movimiento: e.target.value})}
                    required
                  >
                    <option value="entrada">Entrada</option>
                    <option value="salida">Salida</option>
                    <option value="ajuste">Ajuste</option>
                    <option value="traspaso">Traspaso</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Bodega Origen *</label>
                  <select
                    value={formData.bodega}
                    onChange={(e) => setFormData({...formData, bodega: e.target.value})}
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {bodegas.map(b => (
                      <option key={b.id} value={b.id}>{b.nombre}</option>
                    ))}
                  </select>
                </div>

                {formData.tipo_movimiento === 'traspaso' && (
                  <div className="form-group">
                    <label>Bodega Destino *</label>
                    <select
                      value={formData.bodega_destino}
                      onChange={(e) => setFormData({...formData, bodega_destino: e.target.value})}
                      required
                    >
                      <option value="">Seleccionar...</option>
                      {bodegas.filter(b => b.id !== parseInt(formData.bodega)).map(b => (
                        <option key={b.id} value={b.id}>{b.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Motivo *</label>
                  <select
                    value={formData.motivo}
                    onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                    required
                  >
                    <option value="compra">Compra</option>
                    <option value="devolucion">Devolución</option>
                    <option value="produccion">Producción</option>
                    <option value="venta">Venta</option>
                    <option value="uso_interno">Uso Interno</option>
                    <option value="merma">Merma</option>
                    <option value="ajuste_inventario">Ajuste de Inventario</option>
                    <option value="traspaso_bodega">Traspaso entre Bodegas</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Tipo de Documento</label>
                  <select
                    value={formData.tipo_documento}
                    onChange={(e) => setFormData({...formData, tipo_documento: e.target.value})}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="factura">Factura</option>
                    <option value="orden_compra">Orden de compra</option>
                    <option value="guia_despacho">Guía de despacho</option>
                    <option value="boleta">Boleta</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Nº Documento</label>
                  <input
                    type="text"
                    value={formData.numero_documento}
                    onChange={(e) => setFormData({...formData, numero_documento: e.target.value})}
                    placeholder="Factura, OC, Guía, etc."
                  />
                </div>

                <div className="form-group" style={{gridColumn: '1 / -1'}}>
                  <label>Descripción General</label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                    rows={2}
                    placeholder="Descripción general del movimiento"
                  />
                </div>
              </div>

              {/* Sección para agregar recursos */}
              <hr style={{margin: '20px 0'}} />
              <h3>Recursos del Movimiento</h3>

              {/* Formulario para agregar detalle */}
              <div className="form-grid-3cols">
                <div className="form-group">
                  <label>Recurso *</label>
                  <select
                    value={detalleTemp.recurso}
                    onChange={(e) => handleRecursoChange(e.target.value)}
                  >
                    <option value="">Seleccionar...</option>
                    {recursos.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.nombre} ({r.unidad})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Cantidad *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={detalleTemp.cantidad}
                    onChange={(e) => handleCantidadChange(e.target.value)}
                    placeholder="0.00"
                    style={
                      formData.tipo_movimiento === 'salida' &&
                      stockDisponible !== null &&
                      parseFloat(detalleTemp.cantidad || 0) > stockDisponible
                        ? {borderColor: '#dc2626', backgroundColor: '#fee2e2'}
                        : {}
                    }
                  />
                  {formData.tipo_movimiento === 'salida' && stockDisponible !== null && (
                    <small style={{
                      display: 'block',
                      marginTop: '4px',
                      color: parseFloat(detalleTemp.cantidad || 0) > stockDisponible ? '#dc2626' : '#059669',
                      fontWeight: '500'
                    }}>
                      Stock disponible: {stockDisponible.toFixed(2)}
                      {parseFloat(detalleTemp.cantidad || 0) > stockDisponible && ' ⚠️ Cantidad excede stock'}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Precio Unitario {formData.tipo_movimiento === 'salida' && '(PPP)'}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={detalleTemp.precio_unitario}
                    onChange={(e) => setDetalleTemp({...detalleTemp, precio_unitario: e.target.value})}
                    placeholder="0.00"
                    readOnly={formData.tipo_movimiento === 'salida'}
                    style={formData.tipo_movimiento === 'salida' ? {backgroundColor: '#f3f4f6', cursor: 'not-allowed'} : {}}
                    title={formData.tipo_movimiento === 'salida' ? 'Precio Promedio Ponderado (se autocompleta)' : ''}
                  />
                </div>

                {/* Campos de presupuesto solo para salidas */}
                {formData.tipo_movimiento === 'salida' && (
                  <>
                    <div className="form-group">
                      <label>Proyecto *</label>
                      <select
                        value={detalleTemp.proyecto}
                        onChange={(e) => handleProyectoChange(e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        {proyectos.map(p => (
                          <option key={p.id} value={p.id}>{p.nombre}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Presupuesto de Costo *</label>
                      <select
                        value={detalleTemp.presupuesto_costo}
                        onChange={(e) => handlePresupuestoChange(e.target.value)}
                        disabled={!detalleTemp.proyecto}
                      >
                        <option value="">Seleccionar...</option>
                        {presupuestos.map(p => (
                          <option key={p.id} value={p.id}>{p.nombre}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Área *</label>
                      <select
                        value={detalleTemp.area}
                        onChange={(e) => handleAreaChange(e.target.value)}
                        disabled={!detalleTemp.presupuesto_costo}
                      >
                        <option value="">Seleccionar...</option>
                        {areas.map(a => (
                          <option key={a.id} value={a.id}>{a.nombre}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Partida *</label>
                      <select
                        value={detalleTemp.partida}
                        onChange={(e) => handlePartidaChange(e.target.value)}
                        disabled={!detalleTemp.area}
                      >
                        <option value="">Seleccionar...</option>
                        {partidas.map(p => (
                          <option key={p.id} value={p.id}>{p.nombre}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Recurso Presupuestario *</label>
                      <select
                        value={detalleTemp.presupuesto_costo_recurso}
                        onChange={(e) => setDetalleTemp({...detalleTemp, presupuesto_costo_recurso: e.target.value})}
                        disabled={!detalleTemp.partida}
                      >
                        <option value="">Seleccionar...</option>
                        {recursosPresupuestarios.map(r => (
                          <option key={r.id} value={r.id}>{r.nombre}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div className="form-group" style={{gridColumn: '1 / -1'}}>
                  <label>Observaciones del Item</label>
                  <input
                    type="text"
                    value={detalleTemp.observaciones}
                    onChange={(e) => setDetalleTemp({...detalleTemp, observaciones: e.target.value})}
                    placeholder="Observaciones específicas de este recurso"
                  />
                </div>

                <div className="form-group" style={{gridColumn: '1 / -1', display: 'flex', gap: '1rem'}}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={agregarDetalle}
                  >
                    {editingDetalleIndex !== null ? '✓ Actualizar Recurso' : '➕ Agregar Recurso'}
                  </button>
                  {editingDetalleIndex !== null && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={cancelarEdicion}
                      style={{background: '#fee2e2', color: '#991b1b'}}
                    >
                      ✕ Cancelar
                    </button>
                  )}
                </div>
              </div>

              {/* Lista de recursos agregados */}
              {detalles.length > 0 && (
                <div className="detalles-list">
                  <h4>Recursos Agregados ({detalles.length})</h4>
                  <table className="detalles-table">
                    <thead>
                      <tr>
                        <th>Recurso</th>
                        <th>Cantidad</th>
                        <th>Precio Unit.</th>
                        <th>Subtotal</th>
                        {formData.tipo_movimiento === 'salida' && (
                          <>
                            <th>Proyecto</th>
                            <th>Recurso Presupuestario</th>
                          </>
                        )}
                        <th>Observaciones</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalles.map((det, idx) => (
                        <tr key={idx}>
                          <td>{det.recurso_nombre}</td>
                          <td>{parseFloat(det.cantidad).toFixed(2)} {det.recurso_unidad}</td>
                          <td>${parseFloat(det.precio_unitario || 0).toLocaleString()}</td>
                          <td>${(parseFloat(det.cantidad) * parseFloat(det.precio_unitario || 0)).toLocaleString()}</td>
                          {formData.tipo_movimiento === 'salida' && (
                            <>
                              <td>{det.proyecto_nombre || '-'}</td>
                              <td>{det.presupuesto_costo_recurso_nombre || '-'}</td>
                            </>
                          )}
                          <td>{det.observaciones || '-'}</td>
                          <td>
                            <div style={{display: 'flex', gap: '0.5rem', justifyContent: 'center'}}>
                              <button
                                type="button"
                                className="btn-edit-small"
                                onClick={() => editarDetalle(idx)}
                                title="Editar"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                className="btn-delete-small"
                                onClick={() => eliminarDetalle(idx)}
                                title="Eliminar"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {isEditMode ? 'Actualizar' : 'Registrar'} Movimiento ({detalles.length} recurso{detalles.length !== 1 ? 's' : ''})
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovimientosBodegaPage;
